import {
  Box,
  Button,
  Card,
  CardMedia,
  IconButton,
  Typography,
} from '@mui/material'

import { VisuallyHiddenInput } from '../../uploadFile/VisuallyHiddenFileInput'
import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { updateProfile } from '../../../features/authSlice'
import styles from '../../settingsTabs/venderSettingTabs.module.scss'
import { GeneralPaymentPopup } from '../../popups/GeneralPaymentPopup' 
import { toastHelper } from '../../../util/toastHelper'
import { useVendorOrPlannerSubscriptionAccess } from '../../../hooks'

export const MediaCard = ({
  formData,
  cardStyle,
  setFormData,
  handleFileChange,
  editable,
}) => {
  const dispatch = useDispatch()
  const [editing, setEditing] = useState(false)
  const [showPaymentPopup, setShowPaymentPopup] = useState(false)

  const subscriptionAccess = useVendorOrPlannerSubscriptionAccess()
  const maxMedia = subscriptionAccess?.maxMedia || 1
  const currentCount = formData.media?.length || 0
  const remainingSlots = Math.max(maxMedia - currentCount, 0)
  const isBusinessPro = useMemo(() => ['business-pro', 'business-pro-month'].includes(subscriptionAccess?.payment), [subscriptionAccess?.payment])
  const isAtLimit = currentCount >= maxMedia

  const handleSave = () => {
    dispatch(
      updateProfile({
        media: formData.media,
        imageMediaMap: formData.imageMediaMap,
        containsImage: 'media',
      })
    )
    setEditing(false)
  }

  const removePicture = (indexToRemove) => {
    const image = formData.media[indexToRemove]

    setFormData({
      ...formData,
      media: formData.media.filter((_, idx) => idx !== indexToRemove),
      imageMediaMap: { ...formData.imageMediaMap, [image]: null },
    })
  }

  const showLimitMessage = () => {
    if (isBusinessPro) {
      toastHelper(
        `You've reached the ${maxMedia} photo limit for your Business Pro plan.`,
        'info'
      )
    } else {
      setShowPaymentPopup(true)
    }
  }

  const handleUploadButtonClick = (e) => {
    if (isAtLimit) {
      e.preventDefault()
      e.stopPropagation()
      showLimitMessage()
    }
  }

  const applyMediaFiles = (files) => {
    if (!files.length) return

    if (isAtLimit) {
      showLimitMessage()
      return
    }

    if (files.length > remainingSlots) {
      toastHelper(
        `You can only add ${remainingSlots} more photo${
          remainingSlots === 1 ? '' : 's'
        } for your current plan.`,
        'info'
      )
      if (!isBusinessPro) setShowPaymentPopup(true)
      return
    }

    const dt = new DataTransfer()
    files.forEach((f) => dt.items.add(f))
    handleFileChange({ target: { files: dt.files, value: '' } }, 2)
  }

  const handleMediaInputChange = (e) => {
    applyMediaFiles(Array.from(e.target.files || []))
    e.target.value = ''
  }

  const preventDragDefaults = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    preventDragDefaults(e)
    const dropped = e.dataTransfer?.files
    if (!dropped?.length) return
    applyMediaFiles(Array.from(dropped))
  }

  return (
    <>
      <Card
        sx={cardStyle}
        variant="outlined"
        className={`${styles.vendorCardBusinessInfo} ${styles.mediaInfo} `}
      >
        <div className={styles.infoHeader}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            Media
          </Typography>
          {editable && (
            <Box>
              {!editing && (
                <IconButton
                  component="label"
                  className={styles.editIcon}
                  disableRipple
                  onClick={() => setEditing(true)}
                >
                  <img src="/images/edit-icon.svg" alt="business pic" />
                </IconButton>
              )}
              {editing && <Button onClick={handleSave}>Save</Button>}
            </Box>
          )}
        </div>

        <div className={styles.mediaImgContainer}>
          {editing && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Button
                className={styles.mediaImg}
                component="label"
                variant="outlined"
                onClick={handleUploadButtonClick}
                onDragEnter={preventDragDefaults}
                onDragOver={preventDragDefaults}
                onDrop={handleDrop}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: '#7CA6BD',
                    textAlign: 'center',
                    font: 'var(--body-font-three)',
                  }}
                >
                  Upload photos
                </Typography>
                <VisuallyHiddenInput
                  type="file"
                  accept=".jpg, .png"
                  multiple
                  onChange={handleMediaInputChange}
                />
              </Button>
              <Typography
                variant="caption"
                color={isAtLimit ? 'error' : 'text.secondary'}
              >
              </Typography>
            </Box>
          )}

          {formData.media?.map((pic, idx) => (
            <Card
              key={idx}
              variant="outlined"
              className={styles.mediaImg}
              sx={{
                position: 'relative',
                overflow: 'unset',
                '@media (max-width: 499px)': {
                  margin: '0 auto',
                },
              }}
            >
              <CardMedia
                sx={{ height: '100%', borderRadius: '12px' }}
                image={pic}
              />
              {editing && (
                <button
                  style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-6px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    padding: '0',
                  }}
                  onClick={() => removePicture(idx)}
                >
                  <img src="/images/ImageRemove.svg" alt="" />
                </button>
              )}
            </Card>
          ))}
        </div>
      </Card>

      {showPaymentPopup && (
			<GeneralPaymentPopup
				clickHandler={() => setShowPaymentPopup(false)}
			/>)} 
	</>)}